import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Puzzle } from 'lucide-react';
import { useAppRegistry } from '@/api/queries/useAppRegistry';
import {
  useAllInstalledApps,
  useActivateApp,
  useDeactivateApp
} from '@/api/queries/useInstalledApps';
import { usePendingRowIds } from '@/api/mutations/usePendingRowIds';
import { appKeys } from '@/api/query-keys';
import { mergeAppCatalog, type MergedAppRow } from '@/lib/merge-app-catalog';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { Page, PageHeader, PageContent } from '@bliks-io/ui';
import { ContentHeader } from '@/components/content-header';
import { AppDetailSwitch, AppMasterDetail } from '@/components/app-console/AppMasterDetail';
import { AppSeatsPanel } from './components/AppSeatsPanel';

/**
 * The one tenant-side app surface: system admins switch apps on and off and
 * hand out the named seats of an enabled one. The route guard is the only
 * gate — the API enforces the same restriction.
 */
export function AppCatalogPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  useDocumentTitle('pageTitles.manageApps');
  const { data: registryApps, isLoading: registryLoading } = useAppRegistry();
  const { data: installedApps, isLoading: appsLoading } = useAllInstalledApps();
  const activateMutation = useActivateApp();
  const deactivateMutation = useDeactivateApp();

  const pendingAppIds = usePendingRowIds(appKeys.toggleMutations());

  const apps = useMemo(
    () => mergeAppCatalog(registryApps, installedApps, locale).filter((app) => app.installed),
    [registryApps, installedApps, locale]
  );

  const handleToggle = (app: MergedAppRow) => {
    if (app.enabled) {
      deactivateMutation.mutate(app.appId);
    } else {
      activateMutation.mutate(app.appId);
    }
  };

  const renderHeaderAction = (app: MergedAppRow) => (
    <AppDetailSwitch
      appId={app.appId}
      label={t('common.Apps.activation')}
      checked={app.enabled}
      disabled={pendingAppIds.has(app.appId) || !app.allowed}
      onCheckedChange={() => handleToggle(app)}
    />
  );

  const renderDetail = (app: MergedAppRow) => {
    if (!app.allowed) {
      return <p className="text-sm text-muted-foreground">{t('common.Apps.notAllowedNotice')}</p>;
    }
    return (
      <div className="space-y-3">
        <h3 className="text-sm font-semibold">{t('common.Apps.seatsTitle')}</h3>
        {app.enabled ? (
          <AppSeatsPanel app={app} />
        ) : (
          <p className="text-sm text-muted-foreground">{t('common.Apps.seatsNeedEnabledApp')}</p>
        )}
      </div>
    );
  };

  return (
    <Page>
      <PageHeader />
      <PageContent>
        <ContentHeader
          icon={Puzzle}
          title={t('common.Apps.manageTitle')}
          description={t('common.Apps.manageDescription')}
        />

        <div className="mt-8">
          <AppMasterDetail
            rows={apps}
            emptyMessage={t('common.noItemsFound')}
            isLoading={registryLoading || appsLoading}
            renderHeaderAction={renderHeaderAction}
            renderDetail={renderDetail}
          />
        </div>
      </PageContent>
    </Page>
  );
}
